/*
 * PROD-7828 — pure rules for the Payment details page's "Change history": the edits made from its cards,
 * read from GET /audit/list (teacher_uuid + entity_type, newest first) and worded for Ops.
 */
import { EM_DASH, fmt } from '../../teacher_payments/format';
import { audit_value_text } from '../../teacher_payments/audit/adapters';
import { day_label, month_short, stamp_label } from '../../teacher_payments/base_rate/logic';

export const PAGE_SIZE = 50;

// The audit rows this page's cards write: GST / Free Pass, live date and Earnings (teacher_attr), base rate (base_rate).
export const SOURCES = [
  { key: 'attrs', entity_type: 'teacher_attr' },
  { key: 'rate', entity_type: 'base_rate' }
];

export const list_params = (teacher_uuid, entity_type, before_id) => ({
  teacher_uuid,
  entity_type,
  limit: PAGE_SIZE,
  before_id: before_id || ''
});

const NOT_SET = '(not set)';
const yes_no = (v) => {
  if (v === null || v === undefined) return NOT_SET;
  return Number(v) === 1 ? 'Yes' : 'No';
};
const plain = (v) => (v === null || v === undefined || v === '' ? NOT_SET : String(v));
const live_day = (v) => (v ? day_label(v) : NOT_SET);
const category = (v) => {
  if (v === 'NEW') return 'New teacher';
  if (v === 'OLD') return 'Old teacher';
  return plain(v);
};
const rupees = (v) => (v === null || v === undefined || v === '' ? NOT_SET : fmt(v));
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

const line = (label, old_text, new_text) => `${label}: ${old_text} → ${new_text}`;

const recalc_line = (months) =>
  Array.isArray(months) && months.length
    ? `Months recalculated: ${months.map(month_short).join(', ')}`
    : null;

// GST registered / Free Pass balance / payroll / No Free Pass (POST /teacher_attrs): only the fields that moved.
const attrs_lines = (o, n) => {
  const out = [];
  if (o.is_gst_registered !== n.is_gst_registered) {
    out.push(line('GST registered', yes_no(o.is_gst_registered), yes_no(n.is_gst_registered)));
  }
  if (o.free_pass_balance !== n.free_pass_balance) {
    out.push(line('Free Pass balance', plain(o.free_pass_balance), plain(n.free_pass_balance)));
  }
  // A-28 settings (rows written before them carry neither, so nothing shows).
  if (o.on_payroll !== n.on_payroll) {
    out.push(line('Yoga teacher on payroll', yes_no(o.on_payroll), yes_no(n.on_payroll)));
  }
  if (o.no_free_pass !== n.no_free_pass) {
    out.push(line('No Free Pass', yes_no(o.no_free_pass), yes_no(n.no_free_pass)));
  }
  return out;
};

// Profile live date (POST /teacher_attrs/profile_live_at): the date, the Old/New category if it flipped,
// and the open months it sent for recalculation.
const live_date_lines = (o, n) => {
  const out = [line('Profile live date', live_day(o.profile_live_at), live_day(n.profile_live_at))];
  if (o.category !== n.category)
    out.push(line('Teacher category', category(o.category), category(n.category)));
  const recalc = recalc_line(n.recalc_months);
  if (recalc) out.push(recalc);
  return out;
};

// A-15 Earnings switch (earnings_visible_on / _off, from this card or the Overview bulk bar).
const earnings_lines = (o, n) => [
  line('Teacher can see Earnings', yes_no(o.earnings_visible), yes_no(n.earnings_visible))
];

// Base rate (POST /base_rate): old is the rate that applied from that date before the entry.
const base_rate_lines = (o, n) => {
  const entry = obj(n.entry);
  const from = entry.effective_from_ist || o.effective_date;
  const label = from ? `Base rate from ${day_label(from)}` : 'Base rate';
  const out = [line(label, rupees(o.base_rate), rupees(entry.base_rate))];
  const recalc = recalc_line(n.recalc_months);
  if (recalc) out.push(recalc);
  return out;
};

// "free_pass_balance" -> "Free pass balance": the fallback for a row this page does not know.
const field_label = (field) => {
  const words = String(field || '')
    .split(/[._]/)
    .filter(Boolean)
    .join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Value';
};

const generic_lines = (changes) =>
  (Array.isArray(changes) ? changes : []).map((c) =>
    line(field_label(c && c.field), audit_value_text(c && c.old), audit_value_text(c && c.new))
  );

// The plain-words change list of one audit row.
export const change_lines = (row) => {
  const r = row || {};
  const o = obj(r.old_value);
  const n = obj(r.new_value);
  let out;
  if (r.entity_type === 'teacher_attr' && r.action === 'update') out = attrs_lines(o, n);
  else if (r.entity_type === 'teacher_attr' && r.action === 'profile_live_at')
    out = live_date_lines(o, n);
  else if (
    r.entity_type === 'teacher_attr' &&
    (r.action === 'earnings_visible_on' || r.action === 'earnings_visible_off')
  )
    out = earnings_lines(o, n);
  else if (r.entity_type === 'base_rate') out = base_rate_lines(o, n);
  else out = generic_lines(r.changes);
  return out.length ? out : ['No field changed'];
};

const actor_name = (actor) => (actor && (actor.name || actor.id)) || EM_DASH;

export const history_row_view = (row) => ({
  id: row.id,
  when: row.created_at_ist ? stamp_label(row.created_at_ist) : EM_DASH,
  who: actor_name(row.actor),
  changes: change_lines(row),
  reason: (row.reason || '').trim() || EM_DASH
});

// One source's fetched rows plus a page: an id already held (a double press) is not kept twice.
export const append_source = (source, page) => {
  const prev = source || { rows: [], next_before_id: null };
  const seen = new Set(prev.rows.map((r) => r.id));
  const rows = (page && Array.isArray(page.rows) ? page.rows : []).filter((r) => !seen.has(r.id));
  const next = Number(page && page.next_before_id);
  return {
    rows: prev.rows.concat(rows),
    next_before_id: Number.isFinite(next) && next > 0 ? next : null
  };
};

/*
 * The sources merged newest first (ids share one table, so id order is time order). A source with more
 * pages may still hold rows older than its oldest fetched id, so rows below the highest such cutoff wait
 * for "Show older" rather than appear out of order.
 */
export const merged_history = (sources) => {
  const list = Object.values(sources || {}).filter(Boolean);
  const cutoffs = list.map((s) => s.next_before_id).filter((id) => id);
  const cutoff = cutoffs.length ? Math.max(...cutoffs) : 0;
  const rows = list
    .flatMap((s) => s.rows)
    .filter((r) => r.id >= cutoff)
    .sort((a, b) => b.id - a.id)
    .map(history_row_view);
  return { rows, has_more: cutoffs.length > 0 };
};
